/**
 * The Materiały block's wiring: the overlay read, the editor's state, and the two writes.
 *
 * Decides nothing. `model/materialsStatus` says whose links these are and where a save goes;
 * `model/materialsEditor` says what has been typed and whether it may be sent;
 * `api/materialsMutations` makes the call and drops the catalogue.
 *
 * ── The links on screen are the CATALOGUE's, and they never wait ────────────────────────────
 * `GET /exercise` already resolves them — the library's own, or this trainer's overlay where they
 * have one — so the rows are drawn on the block's first frame. The overlay read answers a different
 * question: which of the two that resolved list IS. Only the SENTENCE and „Przywróć wersję z
 * biblioteki" wait on it, and the block reserves both, so its arrival changes ink and not geometry.
 *
 * ── The editor is created on ENTRY and thrown away on exit ──────────────────────────────────
 * `null` is read mode. Entering builds the reducer's state from the links that are on screen at
 * that moment, so a save followed by a refetch cannot leave a baseline behind describing links
 * nobody is looking at any more. It is also why the state does not have to be reconciled with the
 * read: there is no editor open while the read can change.
 *
 * The hook holds no `key` logic of its own — `ui/ExerciseTheatre` keys the block by the movement's
 * id, so walking to the next exercise remounts it and an editor left open on one movement cannot
 * follow the trainer to another.
 */

import { useReducer, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  exerciseOverlayQueryOptions,
  type ExerciseLink,
  type ExerciseOwnership,
} from '~/entities/exercise-library';
import { ApiError } from '~/shared/api/mutator';

import {
  invalidateExerciseMaterials,
  materialsRefusalKey,
  revertExerciseMaterials,
  saveExerciseMaterials,
} from '../api/materialsMutations';
import {
  materialsEditorReducer,
  materialsEditorState,
  materialsEditorView,
  type MaterialsEditorAction,
  type MaterialsEditorView,
} from '../model/materialsEditor';
import { materialsStatus, type MaterialsStatusView, type OverlayReading } from '../model/materialsStatus';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

export interface ExerciseMaterialsState {
  readonly status: MaterialsStatusView;
  /** The materials as the trainer sees them, straight off the catalogue's row. */
  readonly links: readonly ExerciseLink[];
  /** `null` in read mode. */
  readonly editor: MaterialsEditorView | null;
  readonly dispatch: (action: MaterialsEditorAction) => void;
  /** Enter the editor. `withBlank` opens it with one empty row — what „Dodaj link" means from read mode. */
  readonly edit: (withBlank: boolean) => void;
  readonly cancel: () => void;
  readonly save: () => void;
  readonly revert: () => void;
  /** A write is in flight. Every control in the block stops answering. */
  readonly busy: boolean;
  /** The failed write's sentence, as a KEY, or `null`. */
  readonly refusalKey: string | null;
}

/** What the overlay query has said, in the vocabulary `materialsStatus` reads. */
function overlayReading(query: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: { links?: unknown } | undefined;
}): OverlayReading {
  if (query.isPending) return 'pending';
  if (query.isError) {
    // The designed refusal. An overlay on your OWN exercise does not exist, and the server says so
    // once rather than three times — `exerciseOverlayQueryOptions` carries `retry: 0` for it.
    return query.error instanceof ApiError && query.error.status === 403 ? 'notApplicable' : 'failed';
  }
  // `null` is "inherit"; a list AND `[]` are both an override — the trainer replaced the library's
  // links, possibly with none. That distinction is the whole reason this read exists.
  return (query.data?.links ?? null) === null ? 'inherited' : 'overridden';
}

export function useExerciseMaterials(
  exerciseId: number,
  ownership: ExerciseOwnership,
  links: readonly ExerciseLink[],
): ExerciseMaterialsState {
  const contentLang = useContentLanguage();
  const client = useQueryClient();

  const overlay = useQuery({
    ...exerciseOverlayQueryOptions(exerciseId),
    // An own exercise has no overlay by design, and asking costs a round trip to be told 403.
    enabled: ownership !== 'own',
  });

  // `enabled: false` leaves a query `isPending` for ever, so the enablement predicate travels with
  // the reading — a gate that read `isPending` alone would hold this block on „pending" on every
  // exercise the trainer owns.
  const reading: OverlayReading =
    ownership === 'own' ? 'notApplicable' : overlayReading(overlay);

  const [draft, dispatch] = useReducer(materialsEditorReducer, null, () => materialsEditorState([]));
  const [editing, setEditing] = useState(false);
  const [refusalKey, setRefusalKey] = useState<string | null>(null);

  const status = materialsStatus({ ownership, overlay: reading, linkCount: links.length });

  const write = useMutation({
    mutationFn: async (command: { readonly kind: 'save' | 'revert' }) => {
      if (command.kind === 'revert') {
        await revertExerciseMaterials(exerciseId);
      } else {
        await saveExerciseMaterials({
          exerciseId,
          route: status.route,
          links: materialsEditorView(draft).accepted.map((link) => ({
            url: link.url,
            label: link.label,
          })),
          lang: contentLang,
        });
      }
      // AWAITED inside the mutation, so the block stays busy until the row it is about to redraw
      // has arrived. Resolving earlier closes the editor onto the links it just replaced.
      await invalidateExerciseMaterials(client);
    },
    onSuccess: () => {
      setEditing(false);
      setRefusalKey(null);
    },
    onError: (error: unknown) => {
      setRefusalKey(materialsRefusalKey(error));
    },
  });

  return {
    status,
    links,
    editor: editing ? materialsEditorView(draft) : null,
    dispatch,
    edit: (withBlank) => {
      dispatch({
        type: 'open',
        links: links.map((link) => ({ url: link.url, label: link.label })),
        withBlank,
      });
      setRefusalKey(null);
      setEditing(true);
    },
    cancel: () => {
      setEditing(false);
      setRefusalKey(null);
    },
    save: () => {
      write.mutate({ kind: 'save' });
    },
    revert: () => {
      write.mutate({ kind: 'revert' });
    },
    busy: write.isPending,
    refusalKey,
  };
}
